import type { ColumnDef, SortingState } from "@tanstack/react-table";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AssetGroup, Hoid, HoidPartner, LookupResult } from "./data";

import { Button as ButtonV2 } from "../../Button";
import { ExportCSVButton } from "../../Button/ExportCSVButton";
import DatepickerV2 from "../../DatepickerV2";
import { FieldTrigger } from "../../DatepickerV2/components/FieldTrigger";
import { getShortcutButtonLabel } from "../../DatepickerV2/helpers/shortcutLabel";
import { useDatepickerTranslations } from "../../hooks/useDatepickerTranslations";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  BaseArchiveIcon,
  CopyCheckIcon,
  DotsVerticalIcon,
  EyeIcon,
  FilterIcon,
  InfoIcon,
  LinkIcon,
  PlusIcon,
} from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { SearchInputV2 } from "../../SearchInputV2";
import { StatusPill, StatusPillOverflowChip } from "../../StatusPill";
import { TableDesktop } from "../../Table";
import { showErrorToast, showSuccessToast } from "../../Toast";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { useDebounceFn } from "../../utils/debounce";
import { AnalyticsIconButton } from "../components/AnalyticsIconButton";
import { PeriodValue, useReloadScope } from "../periodLoading";
import { ALL_TIME_START } from "../providers/getDefaultDateRange";
import { StatCard } from "../StatCard";
import { ArchiveProgramModal } from "./ArchiveProgramModal";
import { CreateProgramErrorModal } from "./CreateProgramErrorModal";
import { CreateProgramWizard } from "./CreateProgramWizard";
import {
  fmtDate,
  fmtInt,
  fmtVolExact,
  fmtVolMln,
  fmtVolMlnParts,
  hasUndismissedRejection,
  isRecentlyAdded,
  splitBreakdown,
  splitLabel,
} from "./data";
import { DefaultSetPendingModal } from "./DefaultSetPendingModal";
import { FilterModal } from "./FilterModal";
import { MobileSharedRebateProgramsView } from "./mobile";
import { NoRebateAccountModal } from "./NoRebateAccountModal";
import { ProgramCapModal } from "./ProgramCapModal";
import { ProgramDetailsDrawer } from "./ProgramDetailsDrawer";
import { useSharedRebateProgramsContext } from "./providers/SharedRebateProgramsProvider";
import { ReportLinkCell } from "./ReportLinkCell";
import { TableStateMessage } from "./TableStateMessage";
import { useNoRebateProgramsTranslations } from "./useNoRebateProgramsTranslations";
import { VerifyEmailModal } from "./VerifyEmailModal";

// Filtering/sorting happens server-side (computeProgramsPage) — these are
// no-op stand-ins to satisfy TableDesktop's required FilterFns type.
const NOOP_FILTER_FNS = {
  customGlobalFilter: () => true,
  fuzzyFilter: () => true,
};

// Canvas `.menu__item`: flex row, 10px gap, 10px/12px padding, 8px radius,
// hover `--overlay-opacity-state-hover`. `.menu__icon` is a 16px glyph tinted
// `--icon-tertiary`.
const MENU_ITEM_CLASSES =
  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left hover:bg-surface-tonal-default";
const MENU_ICON_CLASSES = "h-4 w-4 shrink-0 text-icon-tertiary";

/** How long "Copied" shows before reverting, per the canvas's KebabMenu. */
const COPIED_RESET_MS = 3000;

/**
 * Search debounce before the query reaches the context (and so the API). Matches
 * the 1000ms the Clients and Leads reports use, so search feels the same across
 * every report.
 */
const SEARCH_DEBOUNCE_MS = 1000;

const HeaderCell = ({ text, info }: { text: string; info?: string }) => (
  <span className="inline-flex items-center gap-1">
    <Typo element="span" type="bodyXS" textColor="text-text-secondary">
      {text}
    </Typo>
    {info && (
      <TooltipWrapper
        text={info}
        color="blue"
        cursor="help"
        tooltipPlacement="top"
        positionStrategy="fixed"
        tooltipMaxWidth={240}
        tooltipContainerClassName="!text-xs"
      >
        <InfoIcon
          className="h-3.5 w-3.5 shrink-0 [&_path]:stroke-icon-tertiary"
          aria-hidden="true"
          focusable="false"
        />
      </TooltipWrapper>
    )}
  </span>
);

/** Mirrors the drawer's own money-field rendering (`ProgramDetailsDrawer.tsx`): the abbreviated "mln"/"bln" figure is what's shown, with the exact value on hover once it's large enough to be abbreviated at all. */
const MoneyValue = ({ value }: { value: number }) => {
  const formatted = fmtVolMln(value);
  if (Math.abs(value) < 1e6) {
    return (
      <Typo element="span" type="bodyMD">
        {formatted}
      </Typo>
    );
  }
  return (
    <Typo element="span" type="bodyMD">
      <TooltipWrapper
        text={fmtVolExact(value)}
        color="blue"
        positionStrategy="fixed"
        tooltipPlacement="top"
        tooltipMaxWidth={240}
        tooltipContainerClassName="!text-xs"
      >
        <span>{formatted}</span>
      </TooltipWrapper>
    </Typo>
  );
};

const SubIBsCell = ({ partners }: { partners: Hoid["partners"] }) => {
  const { t } = useTranslation();
  if (partners.length === 0) {
    return (
      <Typo element="span" type="bodyMD">
        —
      </Typo>
    );
  }
  const [first, ...rest] = partners;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Typo element="span" type="bodyMD" className="truncate">
        {first.name}
      </Typo>
      {rest.length > 0 && (
        <TooltipWrapper
          id={`subibs-${first.subIbId}`}
          text={rest.map((p) => p.name).join(", ")}
          color="blue"
          tooltipPlacement="top"
          positionStrategy="fixed"
          tooltipMaxWidth={240}
          tooltipContainerClassName="!text-xs"
        >
          <StatusPillOverflowChip
            count={rest.length}
            label={t("subIBReport_moreCount", {
              count: rest.length,
              defaultValue: "+{{count}}",
            })}
          />
        </TooltipWrapper>
      )}
    </span>
  );
};

type ProgramCellProps = {
  hoid: Hoid;
  onOpenDetails: () => void;
  onCopyLink: () => void;
  onViewAnalytics: () => void;
  onArchive: () => void;
  showViewPerformance: boolean;
  hasRejectedNotification: boolean;
};

const ProgramCell = ({
  hoid,
  onOpenDetails,
  onCopyLink,
  onViewAnalytics,
  onArchive,
  showViewPerformance,
  hasRejectedNotification,
}: ProgramCellProps) => {
  const { t } = useTranslation();
  // Copy confirms in place: the item swaps to a green check + "Copied" for 3s
  // rather than closing the menu (canvas `KebabMenu` → `handleItem`).
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Typo element="span" type="headingSM" className="truncate">
          {hoid.rebateProgramLabel}
        </Typo>
        {isRecentlyAdded(hoid.createdAt) && (
          <StatusPill status="New" label={t("subIBReport_newTag", "New")} />
        )}
        <StatusPill
          status={hoid.status}
          label={
            hoid.status === "Archived"
              ? t("subIBReport_statusArchived", "Closed")
              : undefined
          }
        />
        {hasRejectedNotification && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-surface-brand-default"
            aria-hidden="true"
            data-testid={`shared_rebate_programs_rejection_dot_${hoid.id}`}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showViewPerformance && (
          <AnalyticsIconButton
            subject={hoid.rebateProgramLabel}
            onClick={onViewAnalytics}
            className="flex h-6 w-6 items-center justify-center rounded-lg text-icon-brand-default hover:bg-overlay-opacity-state-hover"
          />
        )}
        <PopoverMenu
          place="bottom"
          // Opens rightward from the kebab — the DS PopoverMenu left-aligns a
          // `bottom` panel with its trigger's left edge; our shared default is
          // `end` (opens leftward) for backwards compatibility.
          align="start"
          content={(close) => (
            /* Leading 16px icon per item, `gap-2`, danger item tinted
               `--icon-error` — the canvas's `.menu__item` / `.menu__icon`
               (styles.css) and the same shape as components/KebabMenu.tsx,
               which the Sub IBs tab already builds to. */
            <div className="flex w-56 flex-col gap-0.5 p-2">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onOpenDetails();
                  close();
                }}
                className={MENU_ITEM_CLASSES}
              >
                <EyeIcon className={MENU_ICON_CLASSES} aria-hidden="true" />
                <Typo element="span" type="bodySM">
                  {t("subIBReport_viewProgramDetail", "View details")}
                </Typo>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onCopyLink();
                  setCopied(true);
                  if (copyTimer.current) clearTimeout(copyTimer.current);
                  copyTimer.current = setTimeout(
                    () => setCopied(false),
                    COPIED_RESET_MS,
                  );
                }}
                className={MENU_ITEM_CLASSES}
              >
                {copied ? (
                  // Already `--text-positive-default` (#008500) in the asset —
                  // matches the canvas's `.menu__item--copied .menu__icon`.
                  <CopyCheckIcon
                    className="h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <LinkIcon className={MENU_ICON_CLASSES} aria-hidden="true" />
                )}
                <Typo element="span" type="bodySM">
                  {copied
                    ? t("subIBReport_copied", "Copied")
                    : t("subIBReport_copyReferralLink", "Copy referral link")}
                </Typo>
              </button>
              {hoid.status !== "Archived" && (
                <>
                  <div
                    className="my-1 h-px bg-stroke-primary"
                    role="separator"
                  />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onArchive();
                      close();
                    }}
                    className={MENU_ITEM_CLASSES}
                  >
                    <BaseArchiveIcon
                      className="h-4 w-4 shrink-0 text-icon-error"
                      aria-hidden="true"
                    />
                    <Typo
                      element="span"
                      type="bodySM"
                      textColor="text-text-negative-default"
                    >
                      {t("subIBReport_archiveProgram", "Close rebate program")}
                    </Typo>
                  </button>
                </>
              )}
            </div>
          )}
        >
          <button
            type="button"
            aria-label={t("subIBReport_rowActions", "Row actions")}
            className="flex h-6 w-6 items-center justify-center rounded-lg text-icon-brand-default hover:bg-overlay-opacity-state-hover"
          >
            <DotsVerticalIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </PopoverMenu>
      </div>
    </div>
  );
};

type SharedRebateProgramsViewProps = {
  /**
   * Back affordance for the program drawer, present only when it replaced the
   * Sub IB drawer. The page owns that navigation state, so it supplies the
   * handler.
   */
  onProgramDrawerBack?: () => void;
  lookupSubIb?: (email: string) => Promise<LookupResult>;
  /**
   * Builds the Performance Report deep link for a program's row-level "View
   * performance" action. This tab's own date range is deliberately not part of
   * the link — Performance Report opens on its own default period.
   * Undefined (feature-flagged off, or no builder supplied — e.g. Storybook)
   * falls back to the pre-existing "isn't available yet" toast.
   */
  getPerformanceHref?: (hoid: Hoid) => string | undefined;
  onNavigateToPerformance?: (href: string) => void;
  /**
   * Clients column → Account report deep link. Returning undefined for a row
   * keeps that cell as a plain number with no link affordance; non-undefined
   * turns the cell into a hover-link.
   */
  getClientsHref?: (hoid: Hoid) => string | undefined;
  /** Same as above, for the Total earnings column → Trading report. */
  getTotalEarningsHref?: (hoid: Hoid) => string | undefined;
  /** Mobile-only: Trading report href for the detail sheet's "View client data" button, over the sheet's own date range. */
  getClientDataHref?: (
    hoid: Hoid,
    dateRange: { from: Date; to: Date },
  ) => string | undefined;
  /**
   * Called with the resolved href when the user clicks either cell, so the
   * portal/backoffice can route through TanStack Router. Omitted → the cell
   * falls back to the browser's own anchor navigation (same tab).
   */
  onNavigateToClientsReport?: (href: string) => void;
  /**
   * Desktop toolbar's "New program" button, next to Export CSV — the
   * canvas's final placement post PHASE 1 DESCOPE (14 Aug 2026), replacing
   * the old tab-row button. Defaults on; `SubIBReportPage` turns it off when
   * the (dead, Phase-2-only) Sub IBs tab is enabled, since that legacy layout
   * still renders its own button beside the tab strip.
   */
  showNewProgramButton?: boolean;
  /**
   * The Create Program wizard's Relationship step Comment field is Phase-2
   * (part-4039-master-sub-ib-phase-2) — hidden while the flag is off.
   */
  showRelationshipComment?: boolean;
  phase2Enabled?: boolean;
  showSubIbStatus?: boolean;
  showStepUp?: boolean;
  resumeArchive?: { hoid: Hoid };
  reopenArchive?: { hoid: Hoid; fromDrawer: boolean };
  onReopenArchiveHandled?: () => void;
  /** Live catalogue; null falls back to the mock in `catalogue.ts`. */
  assets?: AssetGroup[] | null;
  /**
   * Deep-link seed (`?rebate_program=<HOID>`): on mount, put this value in
   * the search box and — once a row with a matching `id` appears in `rows`
   * — open that program's detail drawer. Undefined (no param in the URL)
   * leaves the tab's normal empty-search behaviour untouched.
   *
   * Scoped to this tab only — the Sub IBs tab (`SubIBsTable`, behind
   * `masterSubIbPhase2`) has no equivalent prop yet. See the TODO in
   * `apps/portal/src/routes/_authenticated/_IBComplete/sub-ib-report.tsx`.
   */
  initialProgramSearch?: string;
};

export const SharedRebateProgramsView = ({
  onProgramDrawerBack,
  lookupSubIb,
  getPerformanceHref,
  onNavigateToPerformance,
  getClientsHref,
  getTotalEarningsHref,
  getClientDataHref,
  onNavigateToClientsReport,
  showNewProgramButton = true,
  showRelationshipComment,
  phase2Enabled,
  showSubIbStatus,
  showStepUp,
  resumeArchive,
  reopenArchive,
  onReopenArchiveHandled,
  initialProgramSearch,
  assets,
}: SharedRebateProgramsViewProps = {}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Stable for the component's lifetime — anchors the calendar's empty-value
  // panel months (see `calendarStartFrom` below) without retriggering
  // `DatepickerV2`'s value-sync effect on every unrelated re-render.
  const [todayAnchor] = useState(() => new Date());
  // Shared with `getShortcutButtonLabel` below — the label can only resolve a
  // preset name if it reads the same shortcut config the picker renders.
  // `allTime` is inserted first (canvas renders "All time" as the first
  // shortcut) and shares `ALL_TIME_START` with `getDefaultDateRange` so the
  // default value's collapsed label matches this exact shortcut, the same
  // way the previous "Last 30 days" default relied on `DatepickerV2`'s own
  // built-in preset.
  const baseDatepickerConfigs = useDatepickerTranslations();
  const datepickerConfigs = useMemo(
    () => ({
      ...baseDatepickerConfigs,
      shortcuts: {
        allTime: {
          text: t("subIBReport_dateAllTime", "All time"),
          period: {
            start: ALL_TIME_START,
            end: (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return today;
            })(),
          },
        },
        ...baseDatepickerConfigs.shortcuts,
      },
    }),
    [baseDatepickerConfigs, t],
  );
  const reload = useReloadScope();
  const {
    state,
    actions,
    options,
    rows,
    totalCount,
    isLoading,
    isError,
    retryTable,
    // The 5 KPI cards come from GET /v1/master/rebate-programs/stats, not from
    // the rows on screen: `clientAccounts`/`uniqueClients` are deduplicated
    // across every program (and ignore the date range), and the money figures
    // cover all programs — none of which can be re-derived by summing one
    // paginated page. See useSharedRebateProgramsStatsApi in apps/portal.
    stats,
    isStatsError,
    isCsvLoading,
    exportCsv,
    totalProgramsUnfiltered,
    splitEmptyState,
    hasNoRebatePrograms,
    openFilter,
    openProgramDetails,
    selectedProgramId,
    closeProgramDetails,
    capModalOpen,
    openCapModal,
    closeCapModal,
    verifyEmailOpen,
    closeVerifyEmail,
    verifyEmail,
    capLimit,
    statusPending,
    noRebateAccountOpen,
    closeNoRebateAccount,
    defaultSetPendingOpen,
    closeDefaultSetPending,
    createErrorOpen,
    closeCreateError,
    filterOpen,
    closeFilter,
    allPrograms,
    roster,
    wizardOpen,
    wizardPresetSubId,
    closeWizard,
    registerCreatedProgram,
    archiveProgram,
    requestArchiveProgram,
    fetchProgramDetailPatch,
    dismissRejectedNotification,
    requestCreateProgram,
    createProgram,
    createStepUp,
    pendingCreateResume,
    openNoRebateAccount,
    refreshAfterCreate,
    masterRebateAccount,
  } = useSharedRebateProgramsContext();

  const [
    dismissedRejectionNotificationIds,
    setDismissedRejectionNotificationIds,
  ] = useState<Set<string>>(() => new Set());
  const applyDismissedNotifications = useCallback(
    (hoid: Hoid): Hoid => ({
      ...hoid,
      partners: hoid.partners.map((partner) =>
        partner.rejectedNotificationId &&
        dismissedRejectionNotificationIds.has(partner.rejectedNotificationId)
          ? { ...partner, rejectedNotificationId: null }
          : partner,
      ),
    }),
    [dismissedRejectionNotificationIds],
  );
  const visibleRows = useMemo(
    () => rows.map(applyDismissedNotifications),
    [rows, applyDismissedNotifications],
  );
  const visibleAllPrograms = useMemo(
    () => allPrograms.map(applyDismissedNotifications),
    [allPrograms, applyDismissedNotifications],
  );
  const dismissPartnerNotification = useCallback(
    async (partner: HoidPartner) => {
      const notificationId = partner.rejectedNotificationId;
      if (!notificationId || !hasUndismissedRejection(partner)) return;
      setDismissedRejectionNotificationIds((current) => {
        const next = new Set(current);
        next.add(notificationId);
        return next;
      });
      try {
        await dismissRejectedNotification?.(notificationId);
      } catch {
        setDismissedRejectionNotificationIds((current) => {
          const next = new Set(current);
          next.delete(notificationId);
          return next;
        });
        showErrorToast({
          title: t(
            "subIBReport_rejectionNotificationDismissError",
            "Couldn't dismiss rejection notification",
          ),
          description: t(
            "subIBReport_rejectionNotificationDismissErrorDescription",
            "Try again. The notification is still available.",
          ),
        });
      }
    },
    [dismissRejectedNotification, t],
  );

  // rows first: the currently displayed page is what the user actually
  // clicked. allPrograms (the mock's full roster) is only a fallback for the
  // mock/Storybook path, where it's the same dataset computeProgramsPage
  // paginates over anyway.
  // The saved row lets the drawer open on a program the loaded rows don't contain.
  const restoredProgram = reopenArchive?.fromDrawer ? reopenArchive.hoid : null;
  const selectedProgram = useMemo(
    () =>
      visibleRows.find((h) => h.id === selectedProgramId) ??
      visibleAllPrograms.find((h) => h.id === selectedProgramId) ??
      (restoredProgram?.id === selectedProgramId
        ? applyDismissedNotifications(restoredProgram)
        : null),
    [
      visibleRows,
      visibleAllPrograms,
      selectedProgramId,
      restoredProgram,
      applyDismissedNotifications,
    ],
  );

  // Trading volume is the one drawer field the list endpoint can't supply
  // (see SharedRebateProgramsResources.programDetail) — tracked as its own
  // loading/error/success state, never merged into the hoid: `0` is a real,
  // valid trading-volume value, so it can't double as "not loaded yet" or
  // "failed to load" without the drawer showing a fabricated number. The
  // detail endpoint keys off the program's *internal* id (Hoid.programId),
  // not the displayed HOID (selectedProgramId/Hoid.id) — cache by the latter
  // (what the drawer is keyed by) but fetch by the former. The cache key also
  // folds in the date range: a value fetched for one range must never be
  // read back after the range changes.
  /**
   * Search is debounced at 1s before it reaches the context, matching the
   * Clients and Leads reports (`useDebounceFn` at 1000ms around
   * `setSearchQuery`). Committing to the context is what re-derives params and
   * refetches, so without this every keystroke fired a request.
   *
   * The input is driven by this local value rather than `state.searchQuery` so
   * typing stays instant while the commit lags. Seeding once is safe: nothing
   * else writes `searchQuery` for this tab, so the two cannot drift.
   */
  const [searchInput, setSearchInput] = useState(state.searchQuery);
  const [commitSearch, cancelCommitSearch] = useDebounceFn(
    actions.setSearchQuery,
    SEARCH_DEBOUNCE_MS,
  );

  // This view unmounts on tab switch (TabContent renders null when inactive)
  // while the provider above it stays mounted — so a pending commit would
  // otherwise land after the user left, applying a search they abandoned.
  useEffect(() => cancelCommitSearch, [cancelCommitSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      commitSearch(value);
    },
    [commitSearch],
  );

  // Deep link (`?rebate_program=<HOID>`): seed the search box immediately
  // rather than through the usual 1s debounce — this value is a caller-
  // supplied final search, not a keystroke to be typed against. One-shot via
  // the ref so a later table refresh (e.g. the user clears the box) can't
  // re-seed it.
  const appliedInitialSearchRef = useRef(false);
  useEffect(() => {
    if (!initialProgramSearch || appliedInitialSearchRef.current) return;
    appliedInitialSearchRef.current = true;
    setSearchInput(initialProgramSearch);
    cancelCommitSearch();
    actions.setSearchQuery(initialProgramSearch);
  }, [initialProgramSearch, actions, cancelCommitSearch]);

  // Once the deep-linked program's row actually arrives (the search above
  // triggers a refetch, so it isn't in `rows` on the first render), open its
  // drawer. One-shot via the ref: without it, a later refetch that still
  // contains the row (e.g. after the drawer's own Archive action) would
  // reopen a drawer the user has since closed.
  const openedInitialDrawerRef = useRef(false);
  useEffect(() => {
    if (!initialProgramSearch || openedInitialDrawerRef.current) return;
    const match = rows.find((h) => h.id === initialProgramSearch);
    if (!match) return;
    openedInitialDrawerRef.current = true;
    openProgramDetails(match.id);
  }, [initialProgramSearch, rows, openProgramDetails]);

  const [pageSize] = useState(state.pageSize);
  // Archiving is confirmed in a modal (canvas `ArchiveProgramModal`) rather than
  // committing straight from the kebab. Both entry points — the row kebab and
  // the drawer's archive button — funnel through this one instance.
  const [archivingHoid, setArchivingHoid] = useState<Hoid | null>(null);
  const requestArchive = useCallback(
    (hoid: Hoid) => requestArchiveProgram(hoid, setArchivingHoid),
    [requestArchiveProgram],
  );

  const reopenedArchiveRef = useRef(false);
  useEffect(() => {
    if (!reopenArchive || reopenedArchiveRef.current) return;
    reopenedArchiveRef.current = true;
    if (reopenArchive.fromDrawer) openProgramDetails(reopenArchive.hoid.id);
    // oxlint-disable-next-line react/set-state-in-effect
    setArchivingHoid(reopenArchive.hoid);
    onReopenArchiveHandled?.();
  }, [reopenArchive, openProgramDetails, onReopenArchiveHandled]);

  const [archiveResuming, setArchiveResuming] = useState(false);
  const resumedArchiveRef = useRef(false);
  useEffect(() => {
    if (!resumeArchive || resumedArchiveRef.current) return;
    resumedArchiveRef.current = true;
    // oxlint-disable-next-line react/set-state-in-effect
    setArchiveResuming(true);
    setArchivingHoid(resumeArchive.hoid);
  }, [resumeArchive]);

  const resumeArchiveProgram = useCallback(
    async (hoid: Hoid, options?: { signal?: AbortSignal }) => {
      await archiveProgram(hoid, { resumed: true, signal: options?.signal });
      refreshAfterCreate();
      setArchivingHoid(null);
      setArchiveResuming(false);
    },
    [archiveProgram, refreshAfterCreate],
  );

  const handleCopyLink = useCallback(
    (hoid: Hoid) => {
      void navigator.clipboard?.writeText(hoid.referralLink);
      showSuccessToast({
        title: t("subIBReport_linkCopied", "Link copied to clipboard"),
      });
    },
    [t],
  );

  const handleViewAnalytics = useCallback(
    (hoid: Hoid) => {
      const href = getPerformanceHref?.(hoid);
      if (!href) {
        showSuccessToast({
          title: t(
            "subIBReport_analyticsNotAvailable",
            "Analytics for {{program}} isn't available yet",
            { program: hoid.rebateProgramLabel },
          ),
        });
        return;
      }
      if (onNavigateToPerformance) {
        onNavigateToPerformance(href);
        return;
      }
      // No router-aware navigate supplied (e.g. Storybook) — same tab, not
      // "_blank": Performance Report is a route in this app, not an external
      // destination.
      window.open(href, "_self");
    },
    [t, getPerformanceHref, onNavigateToPerformance],
  );

  // Cross-navigation into the Sub IBs tab's own detail drawer is a separate
  // feature area (owned by that tab's provider/state) — stub it here rather
  const columns = useMemo<ColumnDef<Hoid, unknown>[]>(
    () => [
      {
        id: "program",
        accessorKey: "rebateProgramLabel",
        header: () => (
          <HeaderCell text={t("subIBReport_colProgram", "Rebate program")} />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        sortDescFirst: true,
        cell: ({ row }) => (
          <ProgramCell
            hoid={row.original}
            onOpenDetails={() => openProgramDetails(row.original.id)}
            onCopyLink={() => handleCopyLink(row.original)}
            onViewAnalytics={() => handleViewAnalytics(row.original)}
            onArchive={() => requestArchive(row.original)}
            showViewPerformance={Boolean(getPerformanceHref)}
            hasRejectedNotification={row.original.partners.some(
              hasUndismissedRejection,
            )}
          />
        ),
      },
      {
        id: "subibs",
        // TanStack's getCanSort() requires an accessor to consider a column
        // sortable at all — the sort key itself (first partner's name,
        // matching the design's own comparator) is unused: actual ordering
        // happens server-side via computeProgramsPage's "subibs" case.
        accessorFn: (row) => row.partners[0]?.name ?? "",
        header: () => (
          <HeaderCell text={t("subIBReport_colSubIBs", "Sub IB")} />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        sortDescFirst: true,
        cell: ({ row }) => <SubIBsCell partners={row.original.partners} />,
      },
      {
        id: "split",
        // Accessor only satisfies TanStack's getCanSort() — actual ordering
        // happens server-side via computeProgramsPage's "split" case.
        accessorFn: (row) => row.masterSplitPct,
        sortDescFirst: true,
        header: () => (
          <HeaderCell
            text={t("subIBReport_colSplit", "Rebate split")}
            info={t(
              "subIBReport_colSplitInfo",
              "The rebate shows your share first then the share of each Sub IB. Splits are fixed at creation.",
            )}
          />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        // The summary carries a tooltip listing each participant's share —
        // canvas `ProgramsView.tsx` → `ProgramRow` (`splitTip`), handoff §4.2
        // "tooltip lists each share". `tabIndex` keeps it keyboard-reachable
        // per the handoff's a11y section.
        cell: ({ row }) => (
          <TooltipWrapper
            text={splitBreakdown(
              row.original,
              t("subIBReport_splitYou", "You"),
            )}
            color="blue"
            tooltipMaxWidth={240}
            tooltipContainerClassName="!text-xs"
          >
            {/* `Typo` takes no `tabIndex`, so the focusable span wraps it. */}
            <span tabIndex={0}>
              <Typo element="span" type="bodyMD">
                {splitLabel(row.original)}
              </Typo>
            </span>
          </TooltipWrapper>
        ),
      },
      {
        id: "lastPayment",
        accessorKey: "lastPayment",
        sortDescFirst: true,
        header: () => (
          <HeaderCell
            text={t("subIBReport_colLastPayment", "Last payment")}
            info={t(
              "subIBReport_colLastPaymentInfo",
              "Most recent payout date. Not affected by the selected date range.",
            )}
          />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        cell: ({ row }) => {
          const { lastPayment } = row.original;
          return (
            <Typo
              element="span"
              type="bodyMD"
              textColor={
                lastPayment ? "text-text-primary" : "text-text-disabled"
              }
            >
              {fmtDate(lastPayment)}
            </Typo>
          );
        },
      },
      {
        id: "clients",
        accessorFn: (row) => row.uniqueClients,
        sortDescFirst: true,
        header: () => (
          <HeaderCell
            text={t("subIBReport_colClients", "Clients")}
            info={t(
              "subIBReport_colClientsInfo",
              "All-time client data under this rebate program, regardless of the selected date range",
            )}
          />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        cell: ({ row }) => {
          const accountsLabel = t("subIBReport_clientAccountCount", {
            count: row.original.clientAccounts,
            defaultValue_one: "{{count}} account",
            defaultValue_other: "{{count}} accounts",
          });
          const href =
            row.original.uniqueClients > 0
              ? getClientsHref?.(row.original)
              : undefined;
          return (
            <ReportLinkCell
              primary={fmtInt(row.original.uniqueClients)}
              supportive={accountsLabel}
              href={href}
              accessibleName={t(
                "subIBReport_clientsCellAriaLabel",
                "Open Account report filtered to {{program}}",
                { program: row.original.rebateProgramLabel },
              )}
              onNavigate={onNavigateToClientsReport}
              data-testid={`shared_rebate_programs_clients_cell_link_${row.original.id}`}
            />
          );
        },
      },
      {
        id: "totalRebate",
        accessorKey: "totalRebate",
        sortDescFirst: true,
        header: () => (
          <HeaderCell
            text={t("subIBReport_colTotalEarning", "Total earnings")}
            info={t(
              "subIBReport_colTotalEarningInfo",
              "Gross rebate earned from all clients' trading activity before split in the selected time period.",
            )}
          />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        cell: ({ row }) => {
          const href =
            row.original.totalRebate > 0
              ? getTotalEarningsHref?.(row.original)
              : undefined;
          return (
            <PeriodValue width="60%">
              <ReportLinkCell
                primary={<MoneyValue value={row.original.totalRebate} />}
                href={href}
                accessibleName={t(
                  "subIBReport_totalEarningsCellAriaLabel",
                  "Open Trading report filtered to {{program}}",
                  { program: row.original.rebateProgramLabel },
                )}
                onNavigate={onNavigateToClientsReport}
                data-testid={`shared_rebate_programs_total_earnings_cell_link_${row.original.id}`}
              />
            </PeriodValue>
          );
        },
      },
      {
        id: "yourPayout",
        accessorFn: (row) => row.myPayout,
        sortDescFirst: true,
        header: () => (
          <HeaderCell
            text={t("subIBReport_colYourPayout", "Your payout")}
            info={t(
              "subIBReport_colYourPayoutInfo",
              "Total earnings minus what's shared with your Sub IBs in the selected time period.",
            )}
          />
        ),
        meta: { headerClassName: "whitespace-nowrap" },
        cell: ({ row }) => (
          <PeriodValue width="55%">
            <MoneyValue value={row.original.myPayout} />
          </PeriodValue>
        ),
      },
    ],
    [
      t,
      openProgramDetails,
      handleCopyLink,
      handleViewAnalytics,
      requestArchive,
      getPerformanceHref,
      getClientsHref,
      getTotalEarningsHref,
      onNavigateToClientsReport,
    ],
  );

  const sorting: SortingState = useMemo(
    () =>
      state.sortBy != null && state.sortOrder != null
        ? [{ id: state.sortBy, desc: state.sortOrder === "desc" }]
        : [],
    [state.sortBy, state.sortOrder],
  );

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        actions.setSort(null, null);
        return;
      }
      actions.setSort(first.id, first.desc ? "desc" : "asc");
    },
    [actions, sorting],
  );

  const activeFilterCount =
    (state.selected.subIb?.length ? 1 : 0) +
    (state.selected.status?.length ? 1 : 0);

  const dateRangePlaceholder = t(
    "subIBReport_dateRangePlaceholder",
    "Select dates",
  );

  const datepickerValue = {
    startDate: state.dateRange.from,
    endDate: state.dateRange.to,
  };
  const dateAllTimeLabel = t("subIBReport_dateAllTime", "All time");
  const dateShortcutLabel = getShortcutButtonLabel(
    datepickerValue,
    datepickerConfigs,
  );
  // "All time" isn't really a bounded range — showing it as a highlighted
  // 2021-to-today selection when the calendar opens misrepresents it as one.
  // Feed the calendar an empty value instead so it opens on today/next month
  // with nothing highlighted (design ref: PART-4130); the trigger's own label
  // is computed separately from the real `datepickerValue` above, so it still
  // reads "All time" regardless of what the calendar itself is shown.
  const calendarValue =
    dateShortcutLabel === dateAllTimeLabel
      ? { startDate: null, endDate: null }
      : datepickerValue;
  // `DatepickerV2`'s own empty-value fallback opens on *previous*/current
  // month (`previousMonthBy(today)`/`today`) — one month off the design ref's
  // current/next. `startFrom` overrides that, but only wire it for the
  // nulled-out "All time" case: passed unconditionally it would also change
  // where a real same-month range (e.g. "This month") opens its second panel.
  const calendarStartFrom =
    dateShortcutLabel === dateAllTimeLabel ? todayAnchor : undefined;

  const noProgramsText = useNoRebateProgramsTranslations();

  const showNoProgramsPage = splitEmptyState && hasNoRebatePrograms;

  const hasNoRows =
    !isLoading &&
    ((!splitEmptyState && totalProgramsUnfiltered === 0) || totalCount === 0);

  const filteredEmptyComponent = (
    <TableStateMessage
      state="empty"
      title={t("subIBReport_emptyTitle", "No data available")}
      description={t(
        "subIBReport_emptyDescription",
        "Try changing your filters or add a Sub IB",
      )}
    />
  );

  const noProgramsComponent = (
    <TableStateMessage
      state="empty"
      card
      title={noProgramsText.title}
      description={noProgramsText.description}
      action={
        <ButtonV2
          variant="brand"
          size="md"
          isLoading={statusPending}
          onClick={() => requestCreateProgram()}
          data-testid="shared_rebate_programs_empty_new_program_button"
        >
          <div className="flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            {noProgramsText.action}
          </div>
        </ButtonV2>
      }
    />
  );

  // Was missing entirely — a failed table fetch rendered an empty tbody with no
  // explanation and no way back. The Sub IBs tab already wires its own error
  // state (`components/SubIBsTable.tsx`).
  const errorComponent = (
    <TableStateMessage
      state="error"
      title={t("subIBReport_errorTitle", "Unable to load data")}
      description={t("subIBReport_errorDescription", "Refresh to try again")}
      action={
        <ButtonV2 variant="brand" size="md" onClick={retryTable}>
          {t("subIBReport_errorRetry", "Refresh")}
        </ButtonV2>
      }
    />
  );

  return (
    /* No top margin: the tab shell already supplies the canvas's 40px
       tabs→toolbar gap (`.page { gap: 40px }`) via `mt-10` on both tab panels
       (SubIBReport/index.tsx). A stray `mt-1` here made this tab's toolbar sit
       44px down while the Sub IBs tab sat at 40px, so the search field jumped
       4px on tab switch — the canvas has one shared Toolbar and cannot differ. */
    <div>
      {/* Mobile is a separate rendering path (PART-4052), not a CSS reflow of
          the desktop table below — see mobile/index.tsx. The 4 modals/wizard
          past the closing `)}` stay unconditional: they're shared across both
          renderings (archiving, the create-program wizard, the per-Sub-IB cap
          and no-rebate-account gates), so an early return here would silently
          break them on mobile. */}
      {isMobile ? (
        <MobileSharedRebateProgramsView
          onProgramDrawerBack={onProgramDrawerBack}
          onArchive={(hoid) => requestArchive(hoid)}
          onViewAnalytics={handleViewAnalytics}
          getClientDataHref={getClientDataHref}
          onNavigateToClientsReport={onNavigateToClientsReport}
          assets={assets}
          showSubIbStatus={showSubIbStatus}
          phase2Enabled={phase2Enabled}
          onDismissRejectedNotification={dismissPartnerNotification}
          dismissedRejectionNotificationIds={dismissedRejectionNotificationIds}
        />
      ) : (
        <>
          {showNoProgramsPage ? (
            <div className="mt-2">{noProgramsComponent}</div>
          ) : (
            <>
              <div className="flex items-end gap-6">
                {/* Canvas `.search { width: 440px; max-width: 100% }`. Kept identical to
            the Sub IBs toolbar so the field does not resize when switching tabs
            — the canvas serves both views from one shared Toolbar. */}
                <div className="relative w-[440px] max-w-full">
                  <label htmlFor="search" className="sr-only">
                    {t("subIBReport_searchLabel", "Search")}
                  </label>
                  <SearchInputV2
                    id="search"
                    name="search"
                    value={searchInput}
                    onChange={handleSearchChange}
                    placeholder={t(
                      "subIBReport_searchPlaceholder",
                      "Search rebate program",
                    )}
                    clearLabel={t("subIBReport_clearSearch", "Clear search")}
                  />
                </div>
                <TooltipWrapper
                  text={t(
                    "subIBReport_dateRangeInfo",
                    "Filters by the date clients traded. Total earning and your payout reflect trades placed in this range.",
                  )}
                  color="blue"
                  tooltipPlacement="top"
                  positionStrategy="fixed"
                  tooltipMaxWidth={240}
                  tooltipContainerClassName="!text-xs"
                >
                  <DatepickerV2
                    value={calendarValue}
                    startFrom={calendarStartFrom}
                    onChange={(range) =>
                      actions.setDateRange({
                        from: range?.startDate ?? null,
                        to: range?.endDate ?? null,
                      })
                    }
                    useRange
                    showShortcuts
                    // Everything from here down mirrors the `Datepicker` wrapper that
                    // Performance report uses (`packages/ui/src/Datepicker/index.tsx`), so
                    // both report date-range pickers behave identically. This view talks to
                    // `DatepickerV2` directly instead of using that wrapper because the
                    // wrapper pulls in `@pepperstone/pepper-ui-kit` (packages/ui/CLAUDE.md).
                    separator="-"
                    displayFormat="DD MMM YYYY"
                    showFooter
                    // Read-only: the range is picked from the calendar, never typed.
                    readOnly
                    // No future dates — reports only ever cover elapsed periods.
                    maxDate={new Date()}
                    configs={datepickerConfigs}
                    classNames={{
                      footer: () =>
                        "flex items-center justify-end pb-2.5 pt-3 border-t border-stroke-primary [&_div]:rtl:gap-2 [&_button]:rounded-md",
                    }}
                    inputId="programs-date-range"
                    placeholder={dateRangePlaceholder}
                    // `FieldTrigger` brings its own `h-10 min-w-[260px]` sizing, so the
                    // container is just a positioning wrapper.
                    containerClassName="relative"
                    trigger={(triggerProps) => (
                      <FieldTrigger
                        {...triggerProps}
                        allowClear={false}
                        placeholder={dateRangePlaceholder}
                        // Collapses an exact-match range to its preset name, so the
                        // default range reads "All time" rather than two dates.
                        // Computed from the real `datepickerValue`, not the
                        // (possibly nulled-out) `calendarValue` fed to the
                        // calendar itself — the trigger's label must stay correct
                        // even while the open calendar shows nothing selected.
                        shortcutButtonLabel={dateShortcutLabel}
                      />
                    )}
                  />
                </TooltipWrapper>
                <ButtonV2
                  variant="tonal"
                  size="md"
                  onClick={openFilter}
                  // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id selected by packages/e2e; never rename existing ids
                  data-testid="rebate-programs-filter-button"
                >
                  <span className="flex items-center gap-2">
                    <FilterIcon height={16} width={16} />
                    <Typo element="span" type="labelSM">
                      {t("subIBReport_filters", "Filter")}
                    </Typo>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-brand-default px-1.5">
                        <Typo
                          element="span"
                          type="label2XS"
                          textColor="text-text-inverse-primary"
                        >
                          {activeFilterCount}
                        </Typo>
                      </span>
                    )}
                  </span>
                </ButtonV2>
                {/* Server-side export (GET /v1/master/rebate-programs/export) — covers
            every row matching the current filters, not just the rows loaded on
            screen, which is what the previous client-side `<ExportCSV>` was
            limited to. The download and its error toast happen inside the `csv`
            resource; this button only triggers it and reflects its status. */}
                {/* Canvas `.toolbar__actions { gap: 16px }`. */}
                <div className="ml-auto flex items-center gap-4">
                  <ExportCSVButton
                    onClick={() => void exportCsv()}
                    isLoading={isCsvLoading}
                    label={t("subIBReport_exportCsv", "Export CSV")}
                    // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id selected by packages/e2e; never rename existing ids
                    data-testid="rebate-programs-export-button"
                  />
                  {/* PHASE 1 DESCOPE (14 Aug 2026, design canvas): "New program"'s
              final home is the toolbar, right of Export CSV — the old
              tab-row placement (`SubIBReport/index.tsx`) is legacy, kept
              only for the dead Phase-2 Sub IBs tab flag. */}
                  {showNewProgramButton && (
                    <ButtonV2
                      variant="brand"
                      size="md"
                      onClick={() => requestCreateProgram()}
                      // Held until the status read resolves: nothing opens before
                      // then, and a second click would fire a second read.
                      isLoading={statusPending}
                      // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id selected by packages/e2e; never rename existing ids
                      data-testid="rebate-programs-new-program-button"
                    >
                      <div className="flex items-center gap-2">
                        <PlusIcon className="h-4 w-4" />
                        {t("subIBReport_newProgram", "New rebate program")}
                      </div>
                    </ButtonV2>
                  )}
                </div>
              </div>

              {/* 40px above the stats strip and 40px below it, matching the canvas's
          `.page { gap: 40px }` between page sections (`styles.css`) — the Sub IBs
          tab nets the same figures, see SubIBReport/index.tsx.

          `grid grid-cols-1` is required, not cosmetic: as a plain block this
          would collapse the table wrapper's `mt-4` into TableDesktop's own
          `mt-6` and yield 24px instead of 40px. Grid items don't margin-collapse
          (the Sub IBs tab relies on `flex flex-col` for the same reason). Grid
          over flex per packages/ui/CLAUDE.md — grid rows get `min-width: 0`, so
          the horizontally-scrolling stats strip scrolls inside itself instead of
          widening the page. */}
              <div className="mt-10 grid grid-cols-1">
                <div className="-mx-1 overflow-x-auto px-1">
                  <div
                    className="grid auto-cols-fr grid-flow-col gap-6"
                    style={{ minWidth: "640px" }}
                  >
                    <StatCard
                      title={t(
                        "subIBReport_statActivePrograms",
                        "Active programs",
                      )}
                      value={
                        reload.full.stats
                          ? ""
                          : fmtInt(stats?.activePrograms ?? 0)
                      }
                      skeleton={reload.full.stats}
                      isError={isStatsError}
                    />
                    <StatCard
                      title={t(
                        "subIBReport_statClientAccounts",
                        "Client accounts",
                      )}
                      value={
                        reload.full.stats
                          ? ""
                          : fmtInt(stats?.clientAccounts ?? 0)
                      }
                      skeleton={reload.full.stats}
                      isError={isStatsError}
                    />
                    <StatCard
                      title={t(
                        "subIBReport_statUniqueClients",
                        "Unique clients",
                      )}
                      value={
                        reload.full.stats
                          ? ""
                          : fmtInt(stats?.uniqueClients ?? 0)
                      }
                      skeleton={reload.full.stats}
                      isError={isStatsError}
                    />
                    <StatCard
                      // Period-scoped: skeletons on a period change as well as on load.
                      // The canvas flags exactly these two cards (`c.skeleton`); Active
                      // programs and the all-time client counts stay put.
                      skeleton={reload.full.stats || reload.period.stats}
                      title={t(
                        "subIBReport_statTotalEarning",
                        "Total earnings",
                      )}
                      value={
                        reload.full.stats || reload.period.stats
                          ? ""
                          : fmtVolMlnParts(stats?.totalEarning ?? 0).value
                      }
                      unit={fmtVolMlnParts(stats?.totalEarning ?? 0).unit}
                      valueTooltip={
                        !reload.full.stats &&
                        !reload.period.stats &&
                        Math.abs(stats?.totalEarning ?? 0) >= 1e6
                          ? fmtVolExact(stats?.totalEarning ?? 0)
                          : undefined
                      }
                      isError={isStatsError}
                      info={t(
                        "subIBReport_statTotalEarningInfo",
                        "Gross rebate earned from all clients' trading activity before split in the selected time period.",
                      )}
                    />
                    <StatCard
                      skeleton={reload.full.stats || reload.period.stats}
                      title={t("subIBReport_statYourPayout", "Your payout")}
                      value={
                        reload.full.stats || reload.period.stats
                          ? ""
                          : fmtVolMlnParts(stats?.yourPayout ?? 0).value
                      }
                      unit={fmtVolMlnParts(stats?.yourPayout ?? 0).unit}
                      valueTooltip={
                        !reload.full.stats &&
                        !reload.period.stats &&
                        Math.abs(stats?.yourPayout ?? 0) >= 1e6
                          ? fmtVolExact(stats?.yourPayout ?? 0)
                          : undefined
                      }
                      isError={isStatsError}
                      info={t(
                        "subIBReport_statYourPayoutInfo",
                        "Total earnings minus what's shared with your Sub IBs in the selected time period.",
                      )}
                    />
                  </div>
                </div>

                {/* TableDesktop adds its own mt-6 (24px) internally — mt-4 (16px) here
            nets exactly 40px, the same arithmetic the Sub IBs tab uses. */}
                <div className="mt-4">
                  <TableDesktop
                    data={visibleRows}
                    columns={columns}
                    isLoading={reload.full.table}
                    enableServerSidePagination
                    enabledServerSideSorting
                    pageCount={Math.max(1, Math.ceil(totalCount / pageSize))}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    state={{ pagination: { pageIndex: state.page, pageSize } }}
                    onPaginationChange={(updater) => {
                      const next =
                        typeof updater === "function"
                          ? updater({ pageIndex: state.page, pageSize })
                          : updater;
                      actions.setPage(next.pageIndex);
                    }}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    onRowClick={(hoid) => openProgramDetails(hoid.id)}
                    filterFns={NOOP_FILTER_FNS}
                    cellContainerClassName="!p-4"
                    rowClassName="h-20"
                    emptyComponent={
                      hasNoRows ? filteredEmptyComponent : undefined
                    }
                    isError={isError}
                    errorComponent={errorComponent}
                    onRetry={retryTable}
                  />
                </div>
              </div>
            </>
          )}

          <FilterModal
            open={filterOpen}
            subIbOptions={options.subIb}
            statusOptions={options.status}
            committedSubIb={state.selected.subIb ?? []}
            committedStatus={state.selected.status ?? []}
            onApply={({ subIb, status }) => {
              actions.setSelected("subIb", subIb.length ? subIb : null);
              actions.setSelected("status", status.length ? status : null);
            }}
            onClose={closeFilter}
          />

          {selectedProgram && (
            <ProgramDetailsDrawer
              hoid={selectedProgram}
              // The fetcher, not its state — the drawer owns the call so its loading
              // and error states, and its Refresh, are its own.
              fetchDetail={fetchProgramDetailPatch}
              // One-way: the page's date filter seeds/updates the drawer's own
              // Performance date chip, but the chip never writes back to this.
              pageDateRange={state.dateRange}
              assets={assets}
              showSubIbStatus={showSubIbStatus}
              phase2Enabled={phase2Enabled}
              onClose={closeProgramDetails}
              onDismissRejectedNotification={dismissPartnerNotification}
              // The drawer only ever archives the program it's currently showing
              // (its own hoid.id) — no need to re-look-up by id, and rows (the
              // current server-paginated page) may not contain a just-created
              // session-only program.
              onArchive={() => requestArchive(selectedProgram)}
              onBack={
                onProgramDrawerBack
                  ? () => {
                      // Replace, not stack: close this drawer before the page
                      // restores the Sub IB one.
                      closeProgramDetails();
                      onProgramDrawerBack();
                    }
                  : undefined
              }
            />
          )}
        </>
      )}

      {/* Shared across both renderings — archiving, the create-program
          wizard, and the per-Sub-IB cap / no-rebate-account gates all apply
          regardless of viewport. */}
      <ArchiveProgramModal
        hoid={archivingHoid}
        onClose={() => {
          setArchivingHoid(null);
          setArchiveResuming(false);
        }}
        resume={archiveResuming}
        onResumeArchived={resumeArchiveProgram}
        onArchived={async (hoid, options) => {
          // Awaited so a rejection (surfaced by the modal's own error toast)
          // propagates back to it instead of closing on failure.
          await archiveProgram(hoid, {
            fromDrawer: selectedProgramId === hoid.id,
            signal: options?.signal,
            onRedirect: options?.onRedirect,
          });
          setArchivingHoid(null);
        }}
        isMobile={isMobile}
        showStepUp={showStepUp}
      />

      {/* capLimit is null only before any status read has resolved. Both paths
          that raise this modal — the pre-flight check and the create call's 409
          — are downstream of one, so it is always set by the time it opens. */}
      {capLimit !== null && (
        <ProgramCapModal
          open={capModalOpen}
          cap={capLimit}
          onClose={closeCapModal}
          onManage={closeCapModal}
          isMobile={isMobile}
        />
      )}

      <NoRebateAccountModal
        open={noRebateAccountOpen}
        onClose={closeNoRebateAccount}
        isMobile={isMobile}
      />

      <DefaultSetPendingModal
        open={defaultSetPendingOpen}
        onClose={closeDefaultSetPending}
        isMobile={isMobile}
      />

      <VerifyEmailModal
        open={verifyEmailOpen}
        onClose={closeVerifyEmail}
        email={verifyEmail?.email}
        verification={verifyEmail}
        isMobile={isMobile}
      />

      <CreateProgramErrorModal
        open={createErrorOpen}
        onClose={closeCreateError}
        isMobile={isMobile}
      />

      {/* Mounted only while open, so every "New program" starts from a clean
          wizard. The wizard's own state lives in useCreateProgramWizard, and
          `if (!open) return null` hides it without unmounting — so a kept-alive
          instance reopens on whatever step it was left on (after a successful
          create, the success screen). Discarding already means losing the work,
          so a fresh instance per open is also what the discard prompt implies. */}
      {wizardOpen && (
        <CreateProgramWizard
          open
          presetSub={roster.find((s) => s.id === wizardPresetSubId) ?? null}
          lookupSubIb={lookupSubIb}
          onClose={closeWizard}
          onCapped={openCapModal}
          assets={assets}
          createProgram={createProgram}
          createStepUp={createStepUp}
          resume={pendingCreateResume}
          onNoRebateAccount={openNoRebateAccount}
          masterRebateAccount={masterRebateAccount}
          onCreated={(result, partnerSpecs) => {
            registerCreatedProgram(
              {
                id: result.hoidId,
                // The internal rebate_program.id, which the detail and archive
                // endpoints key off. Only the real API supplies it; in mock mode
                // it stays undefined and `id` doubles as both.
                programId: result.programId,
                rebateProgramLabel: result.rebateProgramLabel,
                status: "Active",
                rebateSplitPct: result.partners[0]?.splitPct ?? 0,
                masterSplitPct:
                  100 - result.partners.reduce((s, p) => s + p.splitPct, 0),
                accountNumber: "—",
                referralLink: result.referralLink,
                lastPayment: null,
                clientAccounts: 0,
                uniqueClients: 0,
                tradingVolume: 0,
                totalRebate: 0,
                myPayout: 0,
                subIbPayout: 0,
                createdAt:
                  result.createdAt ?? new Date().toISOString().slice(0, 10),
                partners: result.partners.map((p) => ({
                  subIbId: p.canonicalId,
                  name: p.displayName,
                  kycStatus: "Verified",
                  splitPct: p.splitPct,
                  payout: 0,
                })),
              },
              partnerSpecs,
            );
            // registerCreatedProgram only feeds the mock roster; when a real table
            // resource is injected the rows come from the API and never consult
            // it, so the server has to be re-asked for the new row to appear.
            refreshAfterCreate();
          }}
          onViewProgram={(hoidId) => {
            closeWizard();
            openProgramDetails(hoidId);
          }}
          showRelationshipComment={showRelationshipComment}
          phase2Enabled={phase2Enabled}
          showStepUp={showStepUp}
        />
      )}
    </div>
  );
};
